import React, { useState } from 'react'
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Chip
} from '@mui/material'
import {
  Edit as EditIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material'
import { useStyles } from './style'

/**
 * EditablePageHeader Component
 *
 * Displays the page breadcrumb and title with an editable feature for non-default pages.
 * Features:
 * - Back navigation button with tooltip
 * - Two-line breadcrumb layout ("Pages /" on first line, page name on second)
 * - Page status chip (Published/Draft)
 * - Hover-triggered edit icon for page name editing
 * - Conditional edit functionality based on page type and edit mode
 */
const EditablePageHeader = ({ page, onPageNameEdit, isEdit, onBack }) => {
  const classes = useStyles()

  // Tracks hover state for showing/hiding the edit icon
  const [isHovered, setIsHovered] = useState(false)

  /**
   * Handles click on the edit icon
   * Opens the page name edit modal if the callback is provided
   */
  const handleEditClick = () => {
    if (onPageNameEdit) {
      onPageNameEdit(page)
    }
  }

  // Only show edit functionality for non-default pages and when in edit mode
  // Default pages (like homepage) cannot have their names edited
  const canEdit = isEdit && !page.isDefault

  return (
    <Box className={classes.headerContainer}>
      <Box className={classes.backAndTitleContainer}>
        {/* Back Navigation Button */}
        <Tooltip title='Back to pages' placement='top'>
          <IconButton
            onClick={onBack}
            className={classes.backButton}
            data-test='back-to-pages'
            aria-label='back to pages'
            size='medium'
          >
            <BackIcon fontSize='inherit' />
          </IconButton>
        </Tooltip>

        {/* Title Section - Two-line layout */}
        <Box className={classes.titleSection}>
          {/* First line: "Pages /" */}
          <Typography variant='h6' className={classes.location}>
            Pages /
          </Typography>

          {/* Second line: Page name with edit functionality */}
          <Box
            className={classes.titleContainer}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Page title and status chip */}
            <Box className={classes.titleAndStatus}>
              <Typography variant='h5' className={classes.pageTitle}>
                {page.name}
              </Typography>

              {/* Status chip showing Published or Draft */}
              <Chip
                label={page.isPublished ? 'Published' : 'Draft'}
                size='small'
                className={page.isPublished ? classes.publishedChip : classes.draftChip}
                data-test='page-status-chip'
              />
            </Box>

            {/* Edit Icon - Only shown on hover for editable pages */}
            {canEdit && isHovered && (
              <Tooltip title='Edit page name' placement='top'>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    // Prevent event bubbling to avoid triggering parent click handlers
                    e.preventDefault()
                    e.stopPropagation()
                    handleEditClick()
                  }}
                  className={classes.editIcon}
                  data-test='edit-page-name-button'
                >
                  <EditIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default EditablePageHeader
